import { CAT } from '@/shared/CAT';
import type { CanonicalResult } from '@/utils/canonicalTransform';

export function exportOneCSV(all: CanonicalResult) {
  const rows: Record<string,string>[] = [];

  // Header block (top-level categories: Basic/Financial/Address/Usage/Technical)
  const headerObj: Record<string,string> = {};
  const headCats = [CAT.Basic, CAT.Financial, CAT.Address, CAT.Usage, CAT.Technical];
  headCats.forEach(cat => {
    (all.categories[cat] ?? []).forEach(f => {
      headerObj[f.label] = String(f.value ?? '');
    });
  });
  rows.push(headerObj);

  // Identifiers
  (all.identifiers ?? []).forEach(i => rows.push({
    Section: 'Identifier',
    Identifier: i.identifier,
    UtilityType: i.utilityType,
    SiteAddress: i.siteAddress,
    SiteCity: i.siteCity,
    SiteState: i.siteState,
    SiteCountry: i.siteCountry,
    SitePostCode: i.sitePostCode,
    PeriodFrom: i.periodFrom,
    PeriodTo: i.periodTo,
    ServiceTotal: i.serviceTotal != null ? String(i.serviceTotal) : '',
  }));

  // Meters
  (all.meters ?? []).forEach(m => rows.push({
    Section: 'Meter',
    Identifier: m.identifier,
    MeterNumber: m.meterNumber,
    Multiplier: m.multiplier,
    PreviousReading: m.previousReading,
    CurrentReading: m.currentReading,
    Quantity: m.quantity,
    Unit: m.unit,
  }));

  // Line items
  (all.lineItems ?? []).forEach(li => rows.push({
    Section: 'Line Item',
    Identifier: li.identifier,
    MeterNumber: li.meterNumber,
    Description: li.description,
    Quantity: li.quantity,
    Unit: li.unit,
    Rate: li.rate,
    LineTotal: li.total,
  }));

  downloadCSV('energy-bill-data.csv', rows);
}

function downloadCSV(filename: string, rows: Record<string,string>[]) {
  if (rows.length === 0) return;
  
  // Get all unique keys from all rows
  const allKeys = new Set<string>();
  rows.forEach(row => Object.keys(row).forEach(key => allKeys.add(key)));
  const headers = Array.from(allKeys);
  
  // Create CSV content
  const csvContent = [
    headers.join(','),
    ...rows.map(row => 
      headers.map(header => {
        const value = row[header] || '';
        // Escape quotes and wrap in quotes if contains comma, quote, or newline
        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',')
    )
  ].join('\n');
  
  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
